import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { StyledCardSection } from '../../../common/base/Card';
import FilterChip from '../../../common/base/FilterChip';
import Icon from '../../../common/base/Icon';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import Dropdown from '../../../common/Dropdown';
import { DropdownMenuOptionItem } from '../../../common/DropdownMenu';
import { useFieldsSuggestions } from '../../../common/Form/Fields/hooks/useEventPayloadFieldsSuggestions';
import ScrollArea from '../../../common/helpers/ScrollArea';
import { Div } from '../../../common/helpers/StyledUtils';
import JSONBrowser from '../../../common/JSONBrowser';
import type { JSONValue } from '../../../common/JSONBrowser';
import Search from '../../../common/Search';
import { useToasts } from '../../../common/Toast';
import { TableContext } from '../../../contexts/TableContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
const StyledSuggestedColumnsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  column-gap: ${({
  theme
}) => theme.spacing(2)};
  overflow: hidden;
  min-width: 0;
  width: 100%;

  > * {
    flex-shrink: 0;
  }
`;

// Approximate width calculation for a chip based on label length
// FilterChip has: padding (8px each side = 16px) + icon (16px) + gap (4px) + text
// Monospace font at xs size is approximately 7.2px per character
const CHIP_PADDING = 16;
const CHIP_ICON_WIDTH = 20;
const CHAR_WIDTH = 7.2;
const GAP_WIDTH = 8; // column-gap between chips
const MORE_COLUMNS_BUTTON_WIDTH = 120; // "More columns" button approximate width

const estimateChipWidth = (label: string): number => {
  return Math.ceil(CHIP_PADDING + CHIP_ICON_WIDTH + label.length * CHAR_WIDTH);
};
interface ParsedField {
  field_path: string;
  path: (string | number)[];
  display_name: string;
}

// Common popular field names that should be prioritized
const POPULAR_FIELD_NAMES = ['id', 'email', 'name', 'key', 'type', 'status', 'user_id', 'customer_id'];
const parseFieldPath = (field_path: string): ParsedField | null => {
  // Field paths are like "body.sku", "headers.content-type", "query.id", "body.items[*].name", etc.
  const parts = field_path.split('.');
  if (parts.length === 0) return null;
  const root = parts[0];

  // Remove the root part and convert remaining parts to path array
  const path_parts = parts.slice(1);
  const path: (string | number)[] = [];
  for (const part of path_parts) {
    // Handle array notation like "items[*]" -> ["items", 0] or just "items"
    const array_match = part.match(/^(.+)\[\*\]$/);
    if (array_match) {
      // For array notation, we'll use the field name without [*]
      // The actual array handling is done when extracting values
      path.push(array_match[1]);
    } else {
      path.push(part);
    }
  }
  const display_name = path.length > 0 ? `${root}.${path.join('.')}` : root;
  return {
    field_path,
    path,
    display_name
  };
};

// A field's sample value for the hover preview. Some suggested fields have none: paths
// split on dots, so a key with a dot in its name ("user.name", "hub.mode") isn't found.
const PayloadSnippetValue: React.FC<{
  value: JSONValue | undefined;
}> = t0 => {
  const $ = _c(2);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = value === undefined ? <Text size="xs" muted={true}>No sample value</Text> : <JSONBrowser json={value} compact={true} />;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const SuggestedColumns: React.FC<{
  entity: 'requests' | 'events' | 'outpost_events' | 'outpost_attempts';
  models: Record<string, any>[];
}> = t0 => {
  const $ = _c(41);
  const {
    entity,
    models
  } = t0;
  const table_context = useContext(TableContext);
  const {
    addToast
  } = useToasts();
  const [search_term, setSearchTerm] = useState("");
  const [hovered_field_path, setHoveredFieldPath] = useState(null);
  const [hovered_context, setHoveredContext] = useState(null);
  const [preview_value, setPreviewValue] = useState(null);
  let t1;
  if ($[0] !== entity || $[1] !== models) {
    t1 = {
      entity,
      models
    };
    $[0] = entity;
    $[1] = models;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    suggestions,
    getValues,
    getFieldWidth
  } = useFieldsSuggestions(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [recently_used_fields, setRecentlyUsedFields] = useLocalStorage(`suggested-columns-recent-${entity}`, t2);
  const parsed_fields = suggestions.map(parseFieldPath).filter(_temp);
  let t3;
  if ($[4] !== table_context) {
    t3 = field_0 => !table_context.dynamic_columns.some(col => {
      const field_path = col.split(":")[0];
      return field_path === field_0.display_name;
    });
    $[4] = table_context;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const available_fields = parsed_fields.filter(t3);
  const hasPopularName = _temp2;
  let t4;
  if ($[6] !== recently_used_fields) {
    t4 = field_2 => {
      const recently_used_index = recently_used_fields.indexOf(field_2.field_path);
      if (recently_used_index !== -1) {
        return 1000 + (recently_used_fields.length - recently_used_index);
      }
      if (hasPopularName(field_2)) {
        return 500;
      }
      return 0;
    };
    $[6] = recently_used_fields;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const getPriorityScore = t4;
  let t5;
  bb0: {
    if (!search_term) {
      t5 = available_fields;
      break bb0;
    }
    const lower_search = search_term.toLowerCase();
    t5 = available_fields.filter(field_3 => field_3.display_name.toLowerCase().includes(lower_search));
  }
  const filtered_fields = t5;
  let t6;
  if ($[8] !== getPriorityScore) {
    t6 = (a, b) => {
      const score_a = getPriorityScore(a);
      const score_b = getPriorityScore(b);
      return score_b - score_a;
    };
    $[8] = getPriorityScore;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const sorted_primary_fields = available_fields.filter(_temp3).sort(t6);
  const container_ref = useRef(null);
  const [container_width, setContainerWidth] = useState(0);
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const container = container_ref.current;
      if (!container) {
        return;
      }
      const observer = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      observer.observe(container);
      return () => observer.disconnect();
    };
    t8 = [];
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useLayoutEffect(t7, t8);
  let t9;
  bb1: {
    if (container_width === 0) {
      let t10;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = [];
        $[12] = t10;
      } else {
        t10 = $[12];
      }
      t9 = t10;
      break bb1;
    }
    let available_width = container_width - MORE_COLUMNS_BUTTON_WIDTH;
    const fields_that_fit = [];
    for (const field_5 of sorted_primary_fields) {
      const chip_width = estimateChipWidth(field_5.display_name);
      const width_with_gap = chip_width + (fields_that_fit.length > 0 ? GAP_WIDTH : 0);
      if (available_width >= width_with_gap) {
        fields_that_fit.push(field_5);
        available_width = available_width - width_with_gap;
      } else {
        break;
      }
    }
    t9 = fields_that_fit;
  }
  const visible_primary_fields = t9;
  let t10;
  if ($[13] !== addToast || $[14] !== getFieldWidth || $[15] !== setRecentlyUsedFields || $[16] !== table_context) {
    t10 = field_6 => {
      const width = getFieldWidth(field_6.field_path);
      const column_with_width = `${field_6.display_name}:${width}`;
      table_context.addDynamicColumn(column_with_width);
      setRecentlyUsedFields(prev => {
        const updated = [field_6.field_path, ...prev.filter(path => path !== field_6.field_path)];
        return updated.slice(0, 10);
      });
      addToast("success", "Added to table");
    };
    $[13] = addToast;
    $[14] = getFieldWidth;
    $[15] = setRecentlyUsedFields;
    $[16] = table_context;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleAddColumn = t10;
  let t11;
  let t12;
  if ($[18] !== getValues || $[19] !== hovered_field_path) {
    t11 = () => {
      if (hovered_field_path) {
        const preview = getValues(hovered_field_path).at(0);
        setPreviewValue(preview);
      } else {
        setPreviewValue(null);
      }
    };
    t12 = [hovered_field_path, getValues];
    $[18] = getValues;
    $[19] = hovered_field_path;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  if (available_fields.length === 0) {
    let t13;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Div h={{
        px: 28
      }} flex={{
        align: "center"
      }}><Text muted={true} size="s">All suggestions have been added.</Text></Div>;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    return t13;
  }
  let t13;
  if ($[23] !== handleAddColumn || $[24] !== hovered_context || $[25] !== hovered_field_path || $[26] !== preview_value || $[27] !== visible_primary_fields) {
    t13 = visible_primary_fields.map(field_7 => <Tooltip key={field_7.field_path} open={hovered_field_path === field_7.field_path && hovered_context === "primary"} side="bottom" align="right" onHover={() => {
      setHoveredFieldPath(field_7.field_path);
      setHoveredContext("primary");
    }} onLeave={() => {
      setHoveredFieldPath(null);
      setHoveredContext(null);
    }} element={<Div style={{
      maxHeight: "200px",
      width: "300px",
      overflow: "hidden"
    }} p={{
      x: 1
    }}><Text size="xs" semi={true} muted={true} m={{
        b: 1
      }}>Payload snippet</Text><PayloadSnippetValue value={preview_value} /></Div>}><FilterChip label={field_7.display_name} title={field_7.display_name} m={{
        y: 1
      }} dashed={true} mono={true} max_w={{
        px: 300
      }} icon="add" onClick={() => handleAddColumn(field_7)} /></Tooltip>);
    $[23] = handleAddColumn;
    $[24] = hovered_context;
    $[25] = hovered_field_path;
    $[26] = preview_value;
    $[27] = visible_primary_fields;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = opened_0 => {
      if (!opened_0) {
        setSearchTerm("");
      }
    };
    t15 = {
      min: 300,
      max: 300
    };
    $[29] = t14;
    $[30] = t15;
  } else {
    t14 = $[29];
    t15 = $[30];
  }
  let t16;
  if ($[31] !== filtered_fields || $[32] !== handleAddColumn || $[33] !== hovered_context || $[34] !== hovered_field_path || $[35] !== preview_value || $[36] !== search_term) {
    t16 = <Dropdown portal={true} renderToggle={_temp4} onToggle={t14} parent_width={t15}>{toggle => <Div><StyledCardSection><Search borderless={true} small={true} placeholder="Search columns..." value={search_term} onChange={setSearchTerm} /></StyledCardSection>{filtered_fields.length > 0 ? <StyledCardSection><ScrollArea max_h={{
            px: 276
          }} p={1}>{filtered_fields.map(field_8 => <Tooltip key={field_8.field_path} open={hovered_field_path === field_8.field_path && hovered_context === "dropdown"} side="left" fill={true} onHover={() => {
              setHoveredFieldPath(field_8.field_path);
              setHoveredContext("dropdown");
            }} onLeave={() => {
              setHoveredFieldPath(null);
              setHoveredContext(null);
            }} element={<Div style={{
              maxHeight: "200px",
              width: "300px",
              overflow: "hidden"
            }} p={{
              x: 1
            }}><Text size="xs" semi={true} muted={true} m={{
                b: 1
              }}>Payload snippet</Text><PayloadSnippetValue value={preview_value} /></Div>}><DropdownMenuOptionItem label={field_8.display_name} action_icon="add_circle" mono={true} onClick={() => {
                handleAddColumn(field_8);
                toggle(false);
              }} /></Tooltip>)}</ScrollArea></StyledCardSection> : <StyledCardSection p={{
          y: 2.5,
          x: 3
        }}><Text muted={true}><Icon icon="info" left={true} /> {search_term ? "No results..." : "No options..."}</Text></StyledCardSection>}</Div>}</Dropdown>;
    $[31] = filtered_fields;
    $[32] = handleAddColumn;
    $[33] = hovered_context;
    $[34] = hovered_field_path;
    $[35] = preview_value;
    $[36] = search_term;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t13 || $[39] !== t16) {
    t17 = <StyledSuggestedColumnsWrapper ref={container_ref}>{t13}{t16}</StyledSuggestedColumnsWrapper>;
    $[38] = t13;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
};
export default SuggestedColumns;
function _temp(field) {
  return field !== null;
}
function _temp2(field_1) {
  const last_part = field_1.path[field_1.path.length - 1] || field_1.display_name;
  if (typeof last_part !== "string") {
    return false;
  }
  return POPULAR_FIELD_NAMES.includes(last_part.toLowerCase());
}
function _temp3(field_4) {
  return field_4.path.length > 0;
}
function _temp4(opened, handleToggle) {
  return <FilterChip label="More columns" m={{
    y: 1
  }} icon="add" active={opened} dashed={true} mono={true} onClick={() => handleToggle(true)} />;
}